/**
 * Heart organ textures — mechanical/biological hybrid inspired by the
 * BrainPOP "Guts and Bolts" reference.
 *
 * Classic heart-emoji silhouette with deep top dimple between two chunky
 * lobes and a wide-V bottom curve that ends in a center tip. The body
 * texture is intentionally LARGER than the 4×4 cell footprint (240×240
 * vs 200×200) so the lobes and V can extend ~20px past the footprint
 * edges — gives the heart room to look bulky without being constrained
 * by the cell grid. The cones, drawn separately at depth 20 (above the
 * body at depth 10), continue to anchor at the four corner cells and
 * overlay any body that crosses their visual area.
 *
 * Interior details (loosely matching the BrainPOP reference):
 *   - olive/sage/tan paneled tissue, dark olive outline
 *   - salmon wrinkly flank in the upper-left with horizontal hatch lines
 *   - red/yellow/blue jagged vessels threading through the salmon
 *   - large brown muscle lump in the lower-right (the "ventricle blob")
 *   - smaller brown tissue patches scattered around (lower-left, mid)
 *   - fine dark vein tracery and multiple curved panel seams
 *   - a small darker valve in the center
 *   - a stitched dotted seam at the top of the upper-left lobe
 *   - diagonal blue plastic chamber/vial
 *   - two silver bolts (with darker connector stubs) on the right edge
 *   - small white pressure dial in the mid-right
 *
 * Port cells protrude from the four corners (declared by each level's
 * heart entry — see e.g. level7.ts):
 *   TL input  (dCol 0, dRow -1) — venous return (CO₂ blood)
 *   TR input  (dCol 3, dRow -1) — pulmonary return (O₂ blood)
 *   BL output (dCol 0, dRow  3) — CO₂ blood out to lungs (always pumping)
 *   BR output (dCol 3, dRow  3) — O₂ blood out to body (gated on B-in)
 */
import Phaser from 'phaser';
import type { HasTexture } from './core';

// Texture is 240×240 (4 cells × 50px = 200px footprint + 20px overflow on
// each side). All interior coordinates are in texture-local pixels with
// (0,0) at the top-left of the 240×240 canvas. The cell-footprint center
// — where Phaser will place the sprite — is at texture-local (120, 120).
const TEX_SIZE = 240;

export function registerHeartTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-heart-idle')) makeHeart(scene, 'organ-heart-idle', false);
  if (!has('organ-heart-active')) makeHeart(scene, 'organ-heart-active', true);
}

/**
 * Heart-emoji silhouette outline in 240×240 texture coords. Traversed
 * clockwise from the top-center dimple. The body extends past the cell
 * footprint (which would span 20..220 on each axis) up to ~y=10 at the
 * lobe peaks and down to y=230 at the V tip.
 */
const HEART_OUTLINE: ReadonlyArray<[number, number]> = [
  // Deep top dimple between the two lobes
  [120, 58],
  // Right top lobe — chunky and bulbous, peaking high and wide
  [138, 32], [160, 14], [188, 6], [212, 12],
  // Right shoulder — max-width area (just below the lobe peak)
  [228, 28], [237, 50], [240, 75],
  // Body starts tapering inward right after the shoulder
  [238, 100], [232, 122], [222, 142],
  // Aggressive taper down to the V
  [208, 162], [190, 180],
  [170, 196], [148, 210],
  [132, 222],
  // V tip
  [120, 232],
  // Mirror back up
  [108, 222], [92, 210], [70, 196],
  [50, 180], [32, 162], [18, 142],
  [8, 122], [2, 100], [0, 75],
  [3, 50], [12, 28], [28, 12],
  // Left top lobe
  [52, 6], [80, 14], [102, 32],
  // Close back to dimple
  [120, 58],
];

function makeHeart(scene: Phaser.Scene, key: string, active: boolean): void {
  const g = scene.add.graphics();

  // ----- Palette (muted military/medical) -----
  const bodyBase = active ? 0xa8a566 : 0x918e52;
  const bodyLighter = active ? 0xc0bd80 : 0xa8a468;
  const bodyTan = active ? 0xbca874 : 0xa49060;
  const outline = 0x36331a;
  const salmon = active ? 0xeab69e : 0xd99c88;
  const salmonShade = 0xa55a48;
  const brown = active ? 0x8e5e3a : 0x755028;
  const brownDark = 0x4a2e18;
  const veinColor = 0x4a4a20;
  const panelSeam = 0x3c3a18;

  // ===== 1. Drop shadow under the body =====
  g.fillStyle(0x000000, 0.22);
  drawHeartSilhouette(g, 3, 6, 1.0);

  // ===== 2. Outline silhouette (dark olive halo) =====
  g.fillStyle(outline, 1);
  drawHeartSilhouette(g, 0, 0, 1.0);

  // ===== 3. Body fill (inset for outline) =====
  g.fillStyle(bodyBase, 1);
  drawHeartSilhouette(g, 0, 0, 0.965);

  // ===== 4. Tinted color panels (overlapping for variation) =====
  // Each panel is sized to stay within the body silhouette — at the
  // tapered lower half the body is narrow, so the darker panel below
  // is kept tight to the interior to avoid spilling past the outline.
  g.fillStyle(bodyLighter, 0.55);
  g.fillEllipse(120, 75, 160, 70);
  g.fillStyle(bodyTan, 0.45);
  g.fillEllipse(185, 100, 90, 110);

  // ===== 5. Salmon wrinkly flank (upper-left, extending into the top lobe) =====
  g.fillStyle(salmon, 1);
  g.fillEllipse(75, 95, 135, 85);
  g.fillEllipse(108, 75, 70, 55);

  // Horizontal wrinkle hatching
  g.lineStyle(1.5, salmonShade, 0.55);
  for (let i = 0; i < 6; i++) {
    const y = 95 + (i - 2.5) * 7;
    g.beginPath();
    for (let s = 0; s <= 32; s++) {
      const t = s / 32;
      const x = 12 + t * 135;
      const yy = y + Math.sin(t * Math.PI * 3.2 + i * 0.7) * 2.4;
      if (s === 0) g.moveTo(x, yy);
      else g.lineTo(x, yy);
    }
    g.strokePath();
  }

  // ===== 6. Jagged colored vessels (red/yellow/blue) =====
  // Each is a polyline with sharp turns — gives the "twisty wire/vessel"
  // look of the reference (smooth sine waves looked too clean).
  drawJaggedVessel(g, 0xb02838, 3.0, [
    [8, 75], [38, 92], [62, 80], [82, 100], [108, 85], [128, 108], [145, 100],
  ]);
  drawJaggedVessel(g, 0xd8b430, 2.4, [
    [12, 115], [40, 118], [62, 108], [85, 125], [108, 115], [135, 138], [158, 130],
  ]);
  // Blue vessel branches downward across the body (matches reference)
  drawJaggedVessel(g, 0x285ea8, 2.3, [
    [10, 65], [35, 78], [55, 72], [78, 100], [92, 130], [108, 160], [125, 195], [140, 222],
  ]);

  // ===== 7. Brown muscle lump (mid-right, where body is still wide) =====
  // Pulled up from the bottom because the tapered shape doesn't leave
  // room for a big lump in the narrow lower region.
  g.fillStyle(brown, 1);
  g.fillEllipse(165, 148, 75, 58);
  g.fillEllipse(195, 128, 42, 38);

  g.fillStyle(brownDark, 0.45);
  for (const [bx, by, br] of [
    [142, 142, 6], [160, 132, 5], [180, 145, 6], [200, 138, 5],
    [155, 162, 5], [180, 168, 6], [195, 152, 4], [142, 158, 4],
  ] as [number, number, number][]) {
    g.fillCircle(bx, by, br);
  }

  // ===== 8. Fine vein tracery (subtle dark wandering lines) =====
  g.lineStyle(1.1, veinColor, 0.45);
  drawWanderingLine(g, [[25, 50], [55, 65], [85, 55], [115, 70], [145, 55], [175, 75]]);
  drawWanderingLine(g, [[15, 200], [45, 215], [78, 205], [108, 222], [135, 215]]);
  drawWanderingLine(g, [[200, 130], [185, 155], [200, 180], [185, 210]]);

  // ===== 10. Curved panel seams (darker dividing lines) =====
  g.lineStyle(1.7, panelSeam, 0.6);
  drawWanderingLine(g, [
    [15, 145], [50, 138], [90, 150], [130, 138], [170, 148], [210, 138], [230, 145],
  ]);
  drawWanderingLine(g, [[120, 60], [128, 95], [138, 130], [135, 165]]);
  drawWanderingLine(g, [[130, 168], [155, 158], [185, 162], [215, 172]]);
  drawWanderingLine(g, [[60, 215], [88, 222], [120, 218], [155, 222], [180, 212]]);

  // Stitched dotted seam near the top of the upper-left lobe
  g.fillStyle(panelSeam, 0.75);
  for (const [dx, dy] of [
    [35, 60], [48, 56], [60, 54], [72, 54], [85, 57], [98, 62],
  ] as [number, number][]) {
    g.fillCircle(dx, dy, 1.5);
  }

  // ===== 11. Blue plastic chamber/vial (top-center going down-right) =====
  drawBlueChamber(g, 150, 130, 175, 30, active);

  // ===== 12. Two silver bolts on the right edge (with darker stubs) =====
  // Pulled slightly inward so the bolt + outline stays inside the body
  // at the (now narrower) right-shoulder area.
  drawBoltWithStub(g, 213, 60, 12);
  drawBoltWithStub(g, 215, 105, 13);

  // ===== 13. Small darker valve in the center =====
  g.fillStyle(0x4a4828, 1);
  g.fillEllipse(125, 175, 18, 11);
  g.fillStyle(0x1a1808, 1);
  g.fillEllipse(125, 175, 9, 5);

  // ===== 14. Pressure dial (lower-middle, larger) =====
  // Body at y=195 is x≈69-171, so a 16px-radius dial at x=140 fits.
  drawDial(g, 140, 188, 16, active);

  // ===== 15. Sparkles when active (pumping) =====
  if (active) {
    g.fillStyle(0xfff4a8, 1);
    for (const [sx, sy] of [
      [55, 55], [170, 90], [108, 180], [195, 145],
    ] as [number, number][]) {
      g.fillCircle(sx, sy, 2.5);
    }
  }

  g.generateTexture(key, TEX_SIZE, TEX_SIZE);
  g.destroy();
}

/**
 * Stroke the current fillStyle as a closed heart silhouette polygon.
 * Points scaled around the texture center (120, 120) by `scale`, then
 * translated by (dx, dy). Called multiple times to layer drop-shadow,
 * outline halo, and body fill.
 */
function drawHeartSilhouette(
  g: Phaser.GameObjects.Graphics,
  dx: number,
  dy: number,
  scale: number,
): void {
  const cx = 120;
  const cy = 120;
  g.beginPath();
  for (let i = 0; i < HEART_OUTLINE.length; i++) {
    const [px, py] = HEART_OUTLINE[i];
    const x = dx + cx + (px - cx) * scale;
    const y = dy + cy + (py - cy) * scale;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
}

/**
 * Jagged vessel — a chain of straight capsule segments through the given
 * pixel control points. Sharp angles at each control point read as the
 * twisty/bent tubes from the reference, vs. the smoother sine waves the
 * earlier version used.
 */
function drawJaggedVessel(
  g: Phaser.GameObjects.Graphics,
  color: number,
  thickness: number,
  pts: ReadonlyArray<[number, number]>,
): void {
  g.fillStyle(color, 1);
  for (let i = 0; i < pts.length - 1; i++) {
    drawCapsule(g, pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1], thickness);
  }
}

/**
 * Stroke a wandering polyline through `pts` (pixel coords) — used for
 * vein tracery and panel seams. Catmull-Rom smoothed; uses the current
 * lineStyle so the caller controls thickness/color/alpha.
 */
function drawWanderingLine(
  g: Phaser.GameObjects.Graphics,
  pts: ReadonlyArray<[number, number]>,
): void {
  if (pts.length < 2) return;
  const first = pts[0];
  const last = pts[pts.length - 1];
  const pre: [number, number] = [2 * first[0] - pts[1][0], 2 * first[1] - pts[1][1]];
  const post: [number, number] = [
    2 * last[0] - pts[pts.length - 2][0],
    2 * last[1] - pts[pts.length - 2][1],
  ];
  const padded: [number, number][] = [pre, ...(pts as [number, number][]), post];
  g.beginPath();
  g.moveTo(first[0], first[1]);
  const segs = 8;
  for (let i = 1; i < padded.length - 2; i++) {
    const p0 = padded[i - 1];
    const p1 = padded[i];
    const p2 = padded[i + 1];
    const p3 = padded[i + 2];
    for (let s = 1; s <= segs; s++) {
      const t = s / segs;
      const t2 = t * t;
      const t3 = t2 * t;
      const x =
        0.5 *
        (2 * p1[0] +
          (-p0[0] + p2[0]) * t +
          (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
          (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      const y =
        0.5 *
        (2 * p1[1] +
          (-p0[1] + p2[1]) * t +
          (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
          (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
      g.lineTo(x, y);
    }
  }
  g.strokePath();
}

/** Filled capsule from (x1,y1) to (x2,y2) with the given radius. */
function drawCapsule(
  g: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  g.beginPath();
  g.moveTo(x1 + nx * r, y1 + ny * r);
  g.lineTo(x2 + nx * r, y2 + ny * r);
  g.lineTo(x2 - nx * r, y2 - ny * r);
  g.lineTo(x1 - nx * r, y1 - ny * r);
  g.closePath();
  g.fillPath();
  g.fillCircle(x1, y1, r);
  g.fillCircle(x2, y2, r);
}

/**
 * Diagonal blue plastic chamber — outlined capsule with a white highlight
 * stripe and a darker cap rim at the (x2, y2) end. Subtle crinkle ticks
 * suggest the soft-plastic vial texture from the reference.
 */
function drawBlueChamber(
  g: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  active: boolean,
): void {
  const bodyColor = active ? 0x8ec4e2 : 0x6da8cf;
  const highlight = 0xd6ecf8;
  const outlineC = 0x1a3e6a;
  const capColor = 0x88a0b8;
  const r = 16;
  // Outline halo
  g.fillStyle(outlineC, 1);
  drawCapsule(g, x1, y1, x2, y2, r + 2);
  // Body
  g.fillStyle(bodyColor, 1);
  drawCapsule(g, x1, y1, x2, y2, r);
  // Cap at (x2, y2)
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const tx = dx / len;
  const ty = dy / len;
  const nx = -dy / len;
  const ny = dx / len;
  g.fillStyle(capColor, 1);
  drawCapsule(g, x2 - tx * 7, y2 - ty * 7, x2 + tx * 1, y2 + ty * 1, r - 1);
  g.lineStyle(1.5, outlineC, 1);
  g.beginPath();
  g.moveTo(x2 - tx * 7 + nx * (r - 1), y2 - ty * 7 + ny * (r - 1));
  g.lineTo(x2 - tx * 7 - nx * (r - 1), y2 - ty * 7 - ny * (r - 1));
  g.strokePath();
  // White highlight stripe
  const off = -r * 0.4;
  const shrink = 10;
  g.fillStyle(highlight, 0.75);
  drawCapsule(
    g,
    x1 + tx * shrink + nx * off,
    y1 + ty * shrink + ny * off,
    x2 - tx * shrink + nx * off,
    y2 - ty * shrink + ny * off,
    3.5,
  );
  // Crinkle ticks (small perpendicular notches along the length)
  g.lineStyle(1, outlineC, 0.45);
  for (let i = 0; i < 4; i++) {
    const t = 0.25 + i * 0.18;
    const cx = x1 + tx * len * t;
    const cy = y1 + ty * len * t;
    g.beginPath();
    g.moveTo(cx + nx * (r * 0.5), cy + ny * (r * 0.5));
    g.lineTo(cx + nx * (r * 0.85), cy + ny * (r * 0.85));
    g.strokePath();
  }
}

/**
 * Silver bolt with a darker connector stub anchoring it into the body.
 * Matches the reference where each bolt has a small olive post between
 * the body and the bolt head.
 */
function drawBoltWithStub(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number): void {
  // Stub (small darker rectangle from body to bolt)
  g.fillStyle(0x4a4828, 1);
  g.fillRect(x - 12, y - 4, 14, 8);
  g.lineStyle(1.2, 0x2a280c, 1);
  g.strokeRect(x - 12, y - 4, 14, 8);
  // Bolt drop shadow
  g.fillStyle(0x000000, 0.3);
  g.fillCircle(x + 1.5, y + 2.5, r);
  // Bolt body
  g.fillStyle(0xd8d8d8, 1);
  g.fillCircle(x, y, r);
  // Top-left highlight
  g.fillStyle(0xffffff, 0.6);
  g.fillCircle(x - r * 0.35, y - r * 0.35, r * 0.42);
  // Rim
  g.lineStyle(1.8, 0x5a5a5a, 1);
  g.strokeCircle(x, y, r);
  // Cross slot
  g.lineStyle(1.8, 0x3a3a3a, 1);
  g.beginPath();
  g.moveTo(x - r * 0.6, y);
  g.lineTo(x + r * 0.6, y);
  g.moveTo(x, y - r * 0.6);
  g.lineTo(x, y + r * 0.6);
  g.strokePath();
}

/** Pressure dial — off-white face with tick marks and red needle. Radius `r` controls size. */
function drawDial(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  r: number,
  active: boolean,
): void {
  // Shadow + dark rim + face
  g.fillStyle(0x000000, 0.3);
  g.fillCircle(x + 1.5, y + 2.5, r);
  g.fillStyle(0x3a3a3a, 1);
  g.fillCircle(x, y, r);
  g.fillStyle(0xf5f0e8, 1);
  g.fillCircle(x, y, r - 2);
  // Tick marks (8)
  g.lineStyle(Math.max(1, r * 0.12), 0x4a4a4a, 1);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const r1 = r - 2.8;
    const r2 = r - 5;
    g.beginPath();
    g.moveTo(x + Math.cos(a) * r2, y + Math.sin(a) * r2);
    g.lineTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
    g.strokePath();
  }
  // Red needle — swings from rest to pressurized when active
  const needleAngle = active ? Math.PI * 0.2 : -Math.PI * 0.6;
  g.lineStyle(Math.max(1.5, r * 0.16), 0xb02020, 1);
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + Math.cos(needleAngle) * (r - 4), y + Math.sin(needleAngle) * (r - 4));
  g.strokePath();
  // Pivot
  g.fillStyle(0x202020, 1);
  g.fillCircle(x, y, Math.max(1.5, r * 0.16));
}
